import React, { useState, useEffect } from 'react';
import { 
  Plus, 
  Save, 
  Edit, 
  Trash2, 
  DollarSign,
  Trophy,
  Users,
  Clock,
  Calendar,
  X,
  Star,
  Zap,
  Gem,
  Crown,
  Coins,
  Sparkles,
  Award,
  CheckCircle,
  AlertCircle
} from 'lucide-react';
import { database, ref, set, get, update, remove, onValue, push } from '../../firebase';

const RewardSetting = () => {
  // State for different reward types
  const [rewardTypes, setRewardTypes] = useState([
    { id: 'teamsales', name: 'Team Sales', icon: Users, active: true },
    // { id: 'leaderrewards', name: 'Leader Rewards', icon: Trophy, active: false },
    { id: 'weekly', name: 'Weekly', icon: Clock, active: false },
    { id: 'biweekly', name: '14-Day', icon: Calendar, active: false },
    { id: 'monthly', name: 'Monthly', icon: Calendar, active: false }
  ]);

  // Icons for selection in form
  const iconOptions = [
    { name: 'Star', component: Star },
    { name: 'Zap', component: Zap },
    { name: 'Gem', component: Gem },
    { name: 'Crown', component: Crown },
    { name: 'Trophy', component: Trophy },
    { name: 'Coins', component: Coins },
    { name: 'Sparkles', component: Sparkles },
    { name: 'Award', component: Award },
    { name: 'Users', component: Users }
  ];

  // Color options
  const colorOptions = [
    { value: 'text-blue-400', label: 'Blue', bg: 'bg-blue-400/20' },
    { value: 'text-purple-400', label: 'Purple', bg: 'bg-purple-400/20' },
    { value: 'text-pink-400', label: 'Pink', bg: 'bg-pink-400/20' },
    { value: 'text-yellow-400', label: 'Yellow', bg: 'bg-yellow-400/20' },
    { value: 'text-emerald-400', label: 'Green', bg: 'bg-emerald-400/20' },
    { value: 'text-orange-400', label: 'Orange', bg: 'bg-orange-400/20' },
    { value: 'text-red-400', label: 'Red', bg: 'bg-red-400/20' },
    { value: 'text-cyan-400', label: 'Cyan', bg: 'bg-cyan-400/20' },
    { value: 'text-violet-400', label: 'Violet', bg: 'bg-violet-400/20' },
    { value: 'text-amber-400', label: 'Amber', bg: 'bg-amber-400/20' }
  ];

  // State for form inputs
  const [formData, setFormData] = useState({
    type: 'teamsales',
    name: '',
    description: '',
    salesTarget: '',
    periodDays: '',
    rewardType: 'cash',
    cashAmount: '',
    giftValue: '',
    icon: 'Star',
    iconColor: 'text-blue-400',
    status: 'active'
  });

  // State for existing rewards organized by type
  const [rewards, setRewards] = useState({
    teamsales: {},
    // leaderrewards: {},
    weekly: {},
    biweekly: {},
    monthly: {}
  });
  
  const [editingKey, setEditingKey] = useState(null);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState({ show: false, type: '', message: '' });

  // Load all rewards from Firebase
  useEffect(() => {
    const rewardsRef = ref(database, 'rewardSettings');
    
    const unsubscribe = onValue(rewardsRef, (snapshot) => {
      if (snapshot.exists()) {
        const data = snapshot.val();
        console.log('Firebase data loaded:', data);
        
        // Initialize structure
        const organizedRewards = {
          teamsales: {},
          // leaderrewards: {},
          weekly: {},
          biweekly: {},
          monthly: {}
        };
        
        // Organize rewards by type
        // Your data structure: rewardSettings/teamsales/0
        Object.entries(data).forEach(([typeKey, typeData]) => {
          if (typeData) {
            // typeKey is like "teamsales", typeData contains rewards like "0", "1", etc.
            Object.entries(typeData || {}).forEach(([rewardKey, reward]) => {
              if (reward && reward.type) {
                const fullKey = `${typeKey}/${rewardKey}`;
                if (organizedRewards[typeKey]) {
                  organizedRewards[typeKey][fullKey] = reward;
                }
              }
            });
          }
        });
        
        setRewards(organizedRewards);
      } else {
        // Initialize empty structure
        setRewards({
          teamsales: {},
          // leaderrewards: {},
          weekly: {},
          biweekly: {},
          monthly: {}
        });
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  // Get next sequential number for a type
  const getNextNumberForType = (type) => {
    const typeRewards = rewards[type] || {};
    const keys = Object.keys(typeRewards);
    
    if (keys.length === 0) return 0;
    
    // Extract numbers from keys like "teamsales/0", "teamsales/1"
    const numbers = keys.map(key => {
      const parts = key.split('/');
      const num = parseInt(parts[1]);
      return isNaN(num) ? -1 : num;
    }).filter(num => num >= 0);
    
    return numbers.length > 0 ? Math.max(...numbers) + 1 : 0;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleRewardTypeSelect = (type) => {
    setRewardTypes(prev => prev.map(t => ({
      ...t,
      active: t.id === type
    })));
    setFormData(prev => ({
      ...prev,
      type
    }));
  };

  const getIconComponent = (iconName) => {
    const iconMap = {
      Star, Zap, Gem, Crown, Trophy, Coins, Sparkles, Award, Users
    };
    return iconMap[iconName] || Star;
  };

  const showMessage = (type, message) => {
    setShowDialog({ show: true, type, message });
    setTimeout(() => {
      setShowDialog({ show: false, type: '', message: '' });
    }, 3000);
  };

  const validateForm = () => {
    if (!formData.name.trim()) {
      showMessage('error', 'Please enter a reward name');
      return false;
    }
    if (!formData.salesTarget || parseInt(formData.salesTarget) <= 0) {
      showMessage('error', 'Please enter a valid sales target');
      return false;
    }
    if (!formData.cashAmount || parseInt(formData.cashAmount) <= 0) {
      showMessage('error', 'Please enter a valid cash amount');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    const rewardData = {
      type: formData.type,
      name: formData.name.trim(),
      description: formData.description.trim(),
      requirement: {
        salesTarget: parseInt(formData.salesTarget) || 0,
        periodDays: formData.periodDays ? parseInt(formData.periodDays) : 0
      },
      reward: {
        type: formData.rewardType,
        amount: parseInt(formData.cashAmount) || 0,
        giftValue: parseInt(formData.giftValue) || 0
      },
      icon: formData.icon,
      iconColor: formData.iconColor,
      status: formData.status,
      updatedAt: Date.now()
    };

    try {
      if (editingKey) {
        // Update existing reward - editingKey is like "teamsales/0"
        await update(ref(database, `rewardSettings/${editingKey}`), rewardData);
        showMessage('success', 'Reward updated successfully!');
      } else {
        // Add new reward with sequential key
        const nextNumber = getNextNumberForType(formData.type);
        const newKey = `${formData.type}/${nextNumber}`;
        
        await set(ref(database, `rewardSettings/${newKey}`), {
          ...rewardData,
          createdAt: Date.now()
        });
        showMessage('success', 'Reward added successfully!');
      }
      
      resetForm();
      
    } catch (error) {
      console.error('Error saving reward:', error);
      showMessage('error', 'Error saving reward. Please try again.');
    }
  };

  const handleEdit = (key, reward) => {
    // key is like "teamsales/0"
    setFormData({
      type: reward.type || 'teamsales',
      name: reward.name || '',
      description: reward.description || '',
      salesTarget: reward.requirement?.salesTarget || '',
      periodDays: reward.requirement?.periodDays || '',
      rewardType: reward.reward?.type || 'cash',
      cashAmount: reward.reward?.amount || '',
      giftValue: reward.reward?.giftValue || '',
      icon: reward.icon || 'Star',
      iconColor: reward.iconColor || 'text-blue-400',
      status: reward.status || 'active'
    });
    setEditingKey(key);
    setIsFormOpen(true);
    handleRewardTypeSelect(reward.type || 'teamsales');
  };

  const handleDelete = async (key) => {
    if (window.confirm('Are you sure you want to delete this reward?')) {
      try {
        await remove(ref(database, `rewardSettings/${key}`));
        showMessage('success', 'Reward deleted successfully!');
      } catch (error) {
        console.error('Error deleting reward:', error);
        showMessage('error', 'Error deleting reward. Please try again.');
      }
    }
  };

  const resetForm = () => {
    setFormData({
      type: 'teamsales',
      name: '',
      description: '',
      salesTarget: '',
      periodDays: '',
      rewardType: 'cash',
      cashAmount: '',
      giftValue: '',
      icon: 'Star',
      iconColor: 'text-blue-400',
      status: 'active'
    });
    setEditingKey(null);
    setIsFormOpen(false);
  };

  const getCurrentRewards = () => {
    const activeType = rewardTypes.find(t => t.active)?.id;
    const typeRewards = rewards[activeType] || {};
    return Object.entries(typeRewards);
  };

  const getRewardTypeName = () => {
    return rewardTypes.find(t => t.active)?.name || 'Team Sales';
  };

  const getRewardCount = () => {
    return getCurrentRewards().length;
  };

  // Sort rewards by sales target
  const getSortedRewards = () => {
    const currentRewards = getCurrentRewards();
    return currentRewards.sort(([, a], [, b]) => {
      const targetA = a.requirement?.salesTarget || 0;
      const targetB = b.requirement?.salesTarget || 0;
      return targetA - targetB;
    });
  };

  const getIconCount = (type) => {
    return Object.keys(rewards[type] || {}).length;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-navy-dark/50 to-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-silver-light font-heading">Loading rewards...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-navy-dark/50 to-background p-4">
      {/* Success/Error Dialog */}
      {showDialog.show && (
        <div className="fixed top-4 right-4 z-50 animate-slideIn">
          <div className={`rounded-lg p-4 shadow-lg border backdrop-blur-sm ${
            showDialog.type === 'success' 
              ? 'bg-green-500/20 border-green-500/30 text-green-400' 
              : 'bg-red-500/20 border-red-500/30 text-red-400'
          }`}>
            <div className="flex items-center gap-3">
              {showDialog.type === 'success' ? 
                <CheckCircle size={20} /> : 
                <AlertCircle size={20} />
              }
              <span className="font-medium font-heading">{showDialog.message}</span>
            </div>
          </div>
        </div>
      )}

      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-silver-light font-heading mb-2">Reward Settings</h1>
          <p className="text-silver-light font-body">Manage all reward structures and incentives</p>
        </div>

        <div className="flex flex-col lg:flex-row gap-6">
          {/* Left Sidebar - Reward Type Selection */}
          <div className="lg:w-1/4">
            <div className="glass rounded-xl border border-primary/10 p-5 mb-6">
              <h2 className="text-xl font-bold text-silver-light font-heading mb-6 pb-3 border-b border-navy-medium">Reward Types</h2>
              <div className="space-y-3">
                {rewardTypes.map((type) => (
                  <button
                    key={type.id}
                    onClick={() => handleRewardTypeSelect(type.id)}
                    className={`w-full flex items-center gap-4 p-4 rounded-xl transition-all border ${
                      type.active 
                        ? 'bg-primary/15 text-primary border-primary/40 shadow-electric' 
                        : 'hover:bg-navy-medium/30 text-silver-light border-navy-medium hover:border-primary/20'
                    }`}
                  >
                    <type.icon size={22} className="flex-shrink-0" />
                    <span className="font-medium font-heading text-left">{type.name}</span>
                    <span className="ml-auto text-xs bg-navy-medium/50 text-silver px-2 py-1 rounded">
                      {getIconCount(type.id)}
                    </span>
                  </button>
                ))}
              </div>

              {/* Add New Button */}
              <button
                onClick={() => setIsFormOpen(true)}
                className="w-full btn-electric mt-8 py-4 font-heading font-bold text-lg"
              >
                <Plus size={22} className="mr-2" />
                Add New Reward
              </button>
            </div>
          </div>

          {/* Main Content */}
          <div className="lg:w-3/4">
            <div className="glass rounded-xl border border-primary/10 p-5">
              <div className="flex items-center justify-between mb-6 pb-4 border-b border-navy-medium">
                <div>
                  <h2 className="text-2xl font-bold text-silver-light font-heading">{getRewardTypeName()}</h2>
                  <p className="text-silver-light mt-1 font-body">Manage all {getRewardTypeName().toLowerCase()}</p>
                </div>
                <div className="bg-primary/10 text-primary px-4 py-2 rounded-full border border-primary/30">
                  <span className="font-bold font-heading">{getRewardCount()}</span>
                  <span className="ml-1 font-body">rewards</span>
                </div>
              </div>

              {getCurrentRewards().length === 0 ? (
                <div className="text-center py-12">
                  <Trophy className="w-20 h-20 text-navy-medium mx-auto mb-4" />
                  <h3 className="text-xl font-bold text-silver-light font-heading mb-2">No rewards yet</h3>
                  <p className="text-silver-light mb-6 font-body">Get started by adding your first reward</p>
                  <button
                    onClick={() => setIsFormOpen(true)}
                    className="btn-electric px-8 py-3 font-heading"
                  >
                    <Plus size={20} className="mr-2" />
                    Create First Reward
                  </button>
                </div>
              ) : (
                <div className="space-y-4">
                  {getSortedRewards().map(([key, reward]) => {
                    if (!reward) return null;
                    
                    const IconComponent = getIconComponent(reward.icon);
                    const isCashGift = reward.reward?.giftValue > 0;
                    
                    return (
                      <div key={key} className="card-glow p-5 hover:translate-y-[-2px] transition-transform">
                        <div className="flex items-start justify-between mb-4">
                          <div className="flex items-center gap-3">
                            <div className={`p-3 rounded-xl ${reward.iconColor || 'text-blue-400'} bg-navy-medium/30 border border-navy-medium`}>
                              <IconComponent size={24} />
                            </div>
                            <div>
                              <h3 className="font-bold text-lg text-silver-light font-heading">{reward.name || 'Unnamed Reward'}</h3>
                             
                            </div>
                          </div>
                          <div className="flex items-center gap-1">
                            <button
                              onClick={() => handleEdit(key, reward)}
                              className="p-2 text-primary hover:bg-primary/10 rounded-lg transition-colors"
                              title="Edit"
                            >
                              <Edit size={18} />
                            </button>
                            <button
                              onClick={() => handleDelete(key)}
                              className="p-2 text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
                              title="Delete"
                            >
                              <Trash2 size={18} />
                            </button>
                          </div>
                        </div>
                        
                        <div className="grid grid-cols-2 gap-3 mb-4">
                          <div className="bg-navy-medium/30 p-3 rounded-lg">
                            <div className="text-xs text-silver-light font-body mb-1">Target</div>
                            <div className="text-lg font-bold text-primary font-heading">
                              ${(reward.requirement?.salesTarget || 0).toLocaleString()}
                            </div>
                          </div>
                          {reward.requirement?.periodDays > 0 && (
                            <div className="bg-navy-medium/30 p-3 rounded-lg">
                              <div className="text-xs text-silver-light font-body mb-1">Period</div>
                              <div className="text-lg font-bold text-cyan-400 font-heading">
                                {reward.requirement.periodDays} days
                              </div>
                            </div>
                          )}
                        </div>

                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <div className="bg-green-500/10 text-green-400 px-3 py-1 rounded-full text-sm font-medium border border-green-500/20">
                              ${reward.reward?.amount || 0} Cash
                            </div>
                            {isCashGift && (
                              <div className="bg-purple-500/10 text-purple-400 px-3 py-1 rounded-full text-sm font-medium border border-purple-500/20">
                                ${reward.reward?.giftValue || 0} Gift
                              </div>
                            )}
                          </div>
                          <div className={`px-3 py-1 rounded-full text-xs font-medium ${
                            reward.status === 'active' 
                              ? 'bg-green-500/20 text-green-400' 
                              : 'bg-red-500/20 text-red-400'
                          }`}>
                            {reward.status || 'active'}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Form Modal */}
      {isFormOpen && (
        <div className="fixed inset-0 bg-black/90 flex items-start justify-center p-2 sm:p-4 z-50 overflow-y-auto pt-20">
          <div className="bg-gradient-to-b from-navy-dark to-navy-deep rounded-xl shadow-2xl w-full max-w-lg my-auto border border-primary/30">
            {/* Header */}
            <div className="border-b border-navy-medium p-4 sm:p-6 bg-navy-dark/90">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg sm:text-xl font-bold text-white font-heading">
                    {editingKey ? 'Edit Reward' : 'Create Reward'}
                  </h3>
                  <p className="text-xs sm:text-sm text-silver-light mt-1 font-body">
                    {editingKey ? 'Update reward details' : 'Add a new reward to the system'}
                  </p>
                </div>
                <button
                  onClick={resetForm}
                  className="p-2 hover:bg-navy-medium rounded-lg transition-colors text-white"
                >
                  <X size={20} />
                </button>
              </div>
            </div>

            {/* Form Content */}
            <form onSubmit={handleSubmit} className="p-4 sm:p-6 max-h-[80vh] overflow-y-auto">
              <div className="space-y-6">
                {/* Reward Type Selection */}
                <div>
                  <label className="block text-sm font-medium text-silver-light mb-3 font-heading">
                    Reward Type
                  </label>
                  <div className="grid grid-cols-5 gap-2">
                    {rewardTypes.map((type) => (
                      <button
                        type="button"
                        key={type.id}
                        onClick={() => setFormData(prev => ({ ...prev, type: type.id }))}
                        className={`p-2 rounded-lg border transition-all text-xs font-heading flex flex-col items-center gap-1 ${
                          formData.type === type.id
                            ? 'border-primary bg-primary/20 text-white'
                            : 'border-navy-medium hover:bg-navy-medium/50 text-silver-light'
                        }`}
                      >
                        <type.icon size={16} />
                        <span>{type.name}</span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Sales Target & Period */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-silver-light font-heading">
                      Sales Target ($)
                    </label>
                    <input
                      type="number"
                      name="salesTarget"
                      value={formData.salesTarget}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 bg-navy-dark border border-navy-medium rounded-lg focus:ring-2 focus:ring-primary focus:border-primary text-white font-body text-lg"
                      placeholder="1000"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-silver-light font-heading">
                      Period (Days)
                    </label>
                    <input
                      type="number"
                      name="periodDays"
                      value={formData.periodDays}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 bg-navy-dark border border-navy-medium rounded-lg focus:ring-2 focus:ring-primary focus:border-primary text-white font-body text-lg"
                      placeholder="0 for lifetime"
                    />
                  </div>
                </div>

                {/* Reward Name */}
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-silver-light font-heading">
                    Reward Name
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 bg-navy-dark border border-navy-medium rounded-lg focus:ring-2 focus:ring-primary focus:border-primary text-white font-body text-lg"
                    placeholder="e.g., $1,000 Team Sales"
                    required
                  />
                </div>

                {/* Description */}
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-silver-light font-heading">
                    Description
                  </label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 bg-navy-dark border border-navy-medium rounded-lg focus:ring-2 focus:ring-primary focus:border-primary text-white font-body resize-none"
                    placeholder="Describe the reward criteria..."
                    rows="3"
                  />
                </div>

                {/* Cash & Gift Amounts */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-silver-light font-heading">
                      Cash Amount ($)
                    </label>
                    <input
                      type="number"
                      name="cashAmount"
                      value={formData.cashAmount}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 bg-navy-dark border border-navy-medium rounded-lg focus:ring-2 focus:ring-primary focus:border-primary text-white font-body text-lg"
                      placeholder="50"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-silver-light font-heading">
                      Gift Value ($)
                    </label>
                    <input
                      type="number"
                      name="giftValue"
                      value={formData.giftValue}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 bg-navy-dark border border-navy-medium rounded-lg focus:ring-2 focus:ring-primary focus:border-primary text-white font-body text-lg"
                      placeholder="25"
                    />
                  </div>
                </div>

                {/* Icon Selection */}
                <div>
                  <label className="block text-sm font-medium text-silver-light mb-3 font-heading">
                    Select Icon
                  </label>
                  <div className="grid grid-cols-4 sm:grid-cols-5 gap-3">
                    {iconOptions.map(({ name, component: Icon }) => (
                      <button
                        type="button"
                        key={name}
                        onClick={() => setFormData(prev => ({ ...prev, icon: name }))}
                        className={`p-3 rounded-lg border flex flex-col items-center justify-center gap-2 transition-all ${
                          formData.icon === name
                            ? 'border-primary bg-primary/10 text-primary'
                            : 'border-navy-medium hover:bg-navy-medium/30 text-silver-light'
                        }`}
                      >
                        <Icon size={20} />
                        <span className="text-xs font-medium">{name}</span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Icon Color Selection */}
                <div>
                  <label className="block text-sm font-medium text-silver-light mb-3 font-heading">
                    Select Icon Color
                  </label>
                  <div className="grid grid-cols-5 gap-2">
                    {colorOptions.map((color) => {
                      const Icon = getIconComponent(formData.icon);
                      return (
                        <button
                          type="button"
                          key={color.value}
                          onClick={() => setFormData(prev => ({ ...prev, iconColor: color.value }))}
                          className={`p-2 rounded-lg border flex flex-col items-center justify-center gap-1 transition-all ${
                            formData.iconColor === color.value
                              ? 'border-primary bg-primary/10'
                              : 'border-navy-medium hover:bg-navy-medium/30'
                          }`}
                        >
                          <div className={`p-2 rounded ${color.bg}`}>
                            <Icon size={16} className={color.value} />
                          </div>
                          <span className="text-xs text-silver-light">{color.label}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Status */}
                <div>
                  <label className="block text-sm font-medium text-silver-light mb-3 font-heading">
                    Status
                  </label>
                  <div className="flex gap-4">
                    {['active', 'inactive', 'pending'].map((status) => (
                      <label key={status} className="flex items-center">
                        <input
                          type="radio"
                          name="status"
                          value={status}
                          checked={formData.status === status}
                          onChange={handleInputChange}
                          className="mr-2 w-4 h-4 text-primary bg-navy-dark border-navy-medium"
                        />
                        <span className="text-silver-light font-body capitalize">{status}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>

              {/* Form Actions */}
              <div className="flex gap-4 pt-8 mt-6 border-t border-navy-medium">
                <button
                  type="button"
                  onClick={resetForm}
                  className="flex-1 py-3 px-6 border border-navy-medium text-silver-light hover:text-white rounded-lg hover:bg-navy-medium transition-colors font-heading font-medium"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 py-3 px-6 bg-primary hover:bg-primary/90 text-white rounded-lg transition-colors font-heading font-bold shadow-lg shadow-primary/20"
                >
                  <Save size={20} className="mr-2 inline" />
                  {editingKey ? 'Update Reward' : 'Create Reward'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Custom CSS */}
      <style jsx>{`
        input, textarea {
          color: #ffffff !important;
          background-color: #0f172a !important;
          border: 1px solid #334155 !important;
        }
        
        ::placeholder {
          color: #64748b !important;
          opacity: 0.8;
        }
        
        input:focus, textarea:focus {
          border-color: #00b7ff !important;
          box-shadow: 0 0 0 2px rgba(0, 183, 255, 0.2) !important;
          outline: none;
        }
        
        input:-webkit-autofill {
          -webkit-text-fill-color: #ffffff !important;
          -webkit-box-shadow: 0 0 0px 1000px #0f172a inset !important;
          transition: background-color 5000s ease-in-out 0s;
        }
      `}</style>
    </div>
  );
};

export default RewardSetting;